import { useState, useEffect, useCallback, useRef } from "react";
import API from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Switch } from "../ui/switch";
import { ThreadList } from "./ThreadList";
import { ChatBubble } from "./ChatBubble";
import { NoteComposer } from "./NoteComposer";
import { ClientWaitingHint } from "./ClientWaiting";
import { invalidateClientWaiting } from "../../hooks/useClientWaiting";
import { ChatsCircle, LockKey, UsersThree, Check, Checks } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Admin conversation on a job / emergency / project / request: internal + shared team notes, client replies and
   key milestones, including the notes carried over from the originating request (or resulting job). */
export default function NotesThread({ entityType, entityId, folder = "notes", onChanged, title = "Conversation" }) {
  const { user } = useAuth();
  const uid = String(user?._id || "");
  const [items, setItems] = useState([]);
  const [waiting, setWaiting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMilestones, setShowMilestones] = useState(true);
  const composerRef = useRef(null);
  const p = `${entityType}-note`;

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      const { data } = await API.get(`/notes/${entityType}/${entityId}/thread`);
      setItems(data.notes || []);
      setWaiting(data.client_waiting || null);
    } catch { toast.error("Failed to load conversation"); }
    finally { setLoading(false); }
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (entityId) API.post(`/notes/${entityType}/${entityId}/read`).catch(() => {});
  }, [entityType, entityId]);

  const refresh = () => { load(); onChanged?.(); invalidateClientWaiting(); };
  const noteUrl = (n) => `/notes/${n.source?.linked ? n.source.etype : entityType}/${n.source?.linked ? n.source.id : entityId}/${n.id}`;

  const send = async ({ text, attachments, shared }) => {
    const { data } = await API.post(`/notes/${entityType}/${entityId}`, { text, attachments, visible_to_client: shared });
    const mentioned = (data.mentioned_users || []).length;
    toast.success(shared ? "Note shared with the client — they've been notified" : mentioned ? `Internal note added — ${mentioned} colleague${mentioned > 1 ? "s" : ""} notified` : "Internal note added");
    refresh();
  };
  const toggleShare = async (n) => {
    try {
      await API.put(noteUrl(n), { visible_to_client: !n.visible_to_client });
      toast.success(n.visible_to_client ? "Note is now internal" : "Note shared with the client — they've been notified");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update note"); }
  };
  const edit = async (n, text) => {
    try { await API.put(noteUrl(n), { text }); toast.success("Note updated"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to update note"); }
  };
  const del = async (n) => {
    try { await API.delete(noteUrl(n)); toast.success("Note deleted"); refresh(); }
    catch { toast.error("Failed to delete note"); }
  };

  const notes = items.filter((i) => i.kind !== "milestone");
  const sharedCount = notes.filter((n) => n.visible_to_client).length;
  const hasMilestones = items.some((i) => i.kind === "milestone");

  return (
    <div className="space-y-3" data-testid={`notes-thread-${entityType}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <ChatsCircle size={12} /> {title} ({notes.length})
        </h3>
        <span className="flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-400" data-testid={`${p}-internal-count`}><LockKey size={10} weight="bold" /> {notes.length - sharedCount} internal</span>
        <span className="flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400" data-testid={`${p}-shared-count`}><UsersThree size={10} weight="bold" /> {sharedCount} shared with client</span>
        <span className="hidden sm:inline-flex items-center gap-2 text-[10px] text-muted-foreground" data-testid={`${p}-ticks-legend`} title="Delivery status on notes the client can see">
          <span className="inline-flex items-center gap-0.5"><Check size={11} weight="bold" className="text-rose-500" /> sent</span>
          <span className="inline-flex items-center gap-0.5"><Checks size={11} weight="bold" className="text-amber-500" /> received</span>
          <span className="inline-flex items-center gap-0.5"><Checks size={11} weight="bold" className="text-emerald-500" /> read</span>
        </span>
        {hasMilestones && (
          <label className="ml-auto flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer">
            Show milestones <Switch checked={showMilestones} onCheckedChange={setShowMilestones} className="scale-90" data-testid={`${p}-milestones-switch`} />
          </label>
        )}
      </div>
      <ClientWaitingHint waiting={waiting} onReply={() => composerRef.current?.replyShared()} testPrefix={p} />
      {loading ? <p className="text-xs text-muted-foreground py-6 text-center">Loading…</p> : (
        <ThreadList
          items={items}
          showMilestones={showMilestones}
          testPrefix={p}
          emptyText="No notes yet. Internal notes stay with the team; switch to “Shared with client” to post to the client's portal."
          renderNote={(n) => (
            <ChatBubble key={n.id} item={n} mine={n.user_role !== "client"} viewer="admin" myUserId={uid}
              onToggleShare={toggleShare} onEdit={edit} onDelete={del} testPrefix={p} />
          )}
        />
      )}
      <NoteComposer ref={composerRef} onSend={send} folder={folder} allowShare testPrefix={p} />
    </div>
  );
}
